import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Wrench, ArrowLeft, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login'); // login | register | forgot
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
        toast.success('Welcome back!');
        navigate('/');
      } else {
        if (!form.name || !form.email || !form.password) {
          toast.error('Please fill all required fields');
          return;
        }
        if (form.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          return;
        }
        await register(form.name, form.email, form.password, form.phone);
        toast.success('Account created! Welcome to Tools Hub.');
        navigate('/');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error('Please enter your email address'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email: form.email,
        frontend_url: window.location.origin
      });
      setForgotSent(true);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel - decorative */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/1a1b6d87-2606-4c23-8869-de8df060679a/images/a9aa23d080e21c73e35c372654eb037c02db153c7a092318b7c9bfe07c16643a.png"
          alt="Workshop"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 flex flex-col items-start justify-end p-12">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <Wrench size={20} className="text-black" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-black text-xl uppercase tracking-tighter text-white leading-none">M N M TOOLS</div>
              <div className="text-primary text-xs font-body tracking-widest uppercase">Tools Hub</div>
            </div>
          </Link>
          <h2 className="font-heading font-black uppercase text-5xl text-white leading-none mb-4">
            UAE's Trusted<br /><span className="text-primary">Power Tools</span><br />Marketplace
          </h2>
          <p className="text-white/60 text-base max-w-sm">
            Shop genuine Bosch, DeWalt, Makita and more — with UAE's most competitive prices.
          </p>
          <div className="flex gap-6 mt-8">
            {[['35+', 'Products'], ['8+', 'Brands'], ['100%', 'Genuine']].map(([n, l]) => (
              <div key={l}>
                <div className="font-heading font-black text-2xl text-primary">{n}</div>
                <div className="text-xs text-white/40 uppercase tracking-widest">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center px-6 py-12">
        {/* Logo mobile */}
        <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
          <div className="w-9 h-9 bg-primary flex items-center justify-center">
            <Wrench size={18} className="text-black" strokeWidth={2.5} />
          </div>
          <div className="font-heading font-black text-lg uppercase tracking-tighter text-white">M N M TOOLS</div>
        </Link>

        <div className="w-full max-w-sm">
          {/* Back link */}
          <Link to="/" className="flex items-center gap-1 text-muted-foreground hover:text-primary text-xs uppercase tracking-widest mb-6 transition-colors">
            <ArrowLeft size={12} /> Back to Home
          </Link>

          <h1 className="font-heading font-black text-3xl uppercase text-white mb-1">
            {mode === 'login' ? 'Welcome Back' : mode === 'forgot' ? 'Reset Password' : 'Create Account'}
          </h1>
          <p className="text-muted-foreground text-sm mb-8">
            {mode === 'login' ? 'Sign in to your Tools Hub account' : mode === 'forgot' ? 'Enter your email to reset password' : 'Join thousands of UAE professionals'}
          </p>

          {mode === 'forgot' ? (
            <div>
              {forgotSent ? (
                /* Success state */
                <div className="text-center py-4">
                  <div className="w-14 h-14 bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto mb-4">
                    <Mail size={24} className="text-primary" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-white mb-2 uppercase">Check Your Email</h3>
                  <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                    If <span className="text-white">{form.email}</span> is registered, a reset link has been sent. Check your inbox and spam folder.
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">The link expires in 1 hour.</p>
                  <button
                    onClick={() => { setMode('login'); setForgotSent(false); setForm(f => ({ ...f, email: '' })); }}
                    className="w-full bg-surface border border-white/10 text-white font-bold uppercase tracking-wider py-3 hover:border-primary hover:text-primary transition-all text-sm"
                    data-testid="back-to-login-btn"
                  >
                    Back to Login
                  </button>
                </div>
              ) : (
                /* Forgot form */
                <form onSubmit={handleForgotPassword}>
                  <div className="mb-4">
                    <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Email Address</label>
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={form.email}
                      onChange={e => set('email', e.target.value)}
                      className="w-full bg-surface border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-3 outline-none text-sm transition-colors"
                      style={{ borderRadius: 0 }}
                      data-testid="forgot-email-input"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary text-black font-bold uppercase tracking-wider py-3 hover:bg-white transition-colors text-sm disabled:opacity-60"
                    data-testid="forgot-submit-btn"
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('login')}
                    className="w-full mt-3 text-sm text-muted-foreground hover:text-primary transition-colors py-1"
                  >
                    Back to login
                  </button>
                </form>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'register' && (
                <>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Full Name *</label>
                    <input type="text" placeholder="Ahmed Al Rashidi" value={form.name} onChange={e => set('name', e.target.value)}
                      className="w-full bg-surface border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-3 outline-none text-sm transition-colors"
                      style={{ borderRadius: 0 }} data-testid="register-name-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Phone (optional)</label>
                    <input type="tel" placeholder="+971 50 XXX XXXX" value={form.phone} onChange={e => set('phone', e.target.value)}
                      className="w-full bg-surface border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-3 outline-none text-sm transition-colors"
                      style={{ borderRadius: 0 }} data-testid="register-phone-input" />
                  </div>
                </>
              )}

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Email Address *</label>
                <input type="email" placeholder="your@email.com" value={form.email} onChange={e => set('email', e.target.value)}
                  className="w-full bg-surface border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 py-3 outline-none text-sm transition-colors"
                  style={{ borderRadius: 0 }} data-testid="login-email-input" required />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Password *</label>
                <div className="relative">
                  <input type={showPass ? 'text' : 'password'} placeholder={mode === 'login' ? '••••••••' : 'Min. 6 characters'}
                    value={form.password} onChange={e => set('password', e.target.value)}
                    className="w-full bg-surface border border-white/10 focus:border-primary text-white placeholder-white/30 px-4 pr-11 py-3 outline-none text-sm transition-colors"
                    style={{ borderRadius: 0 }} data-testid="login-password-input" required />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    className="absolute right-0 top-0 bottom-0 px-3 text-white/30 hover:text-primary transition-colors">
                    {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>

              {mode === 'login' && (
                <div className="flex justify-end">
                  <button type="button" onClick={() => setMode('forgot')} className="text-xs text-muted-foreground hover:text-primary transition-colors">
                    Forgot password?
                  </button>
                </div>
              )}

              <button type="submit" disabled={loading}
                className="w-full bg-primary text-black font-bold uppercase tracking-wider py-3 hover:bg-white transition-colors text-sm disabled:opacity-60 mt-2"
                data-testid={mode === 'login' ? 'login-submit-btn' : 'register-submit-btn'}>
                {loading ? (mode === 'login' ? 'Signing in...' : 'Creating account...') : (mode === 'login' ? 'Sign In' : 'Create Account')}
              </button>
            </form>
          )}

          {mode !== 'forgot' && (
            <p className="text-center text-sm text-muted-foreground mt-6">
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button
                onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                className="text-primary hover:text-white transition-colors font-semibold"
                data-testid="auth-toggle-btn"
              >
                {mode === 'login' ? 'Register' : 'Sign In'}
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
